import {
  WorkOrderForVehicle,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Empty from '../../../assets/empty.png';
import { OrderTypes, ProcessStep } from '../../../common/enums/enums';
import DownloadPdf from '../../../common/helpers/DownloadPdf';
import {
  getValueIfPresent,
  groupByDateAndType,
  sortGroupedByDate,
  swapKeysAndValues,
} from '../../../helpers/repairOrders';
import { getWorkOrderToPdf } from '../../../redux/actions/userManagementActions';
import {
  setAllOrdersHasMore,
  setAllOrdersSortDetails,
  setAllOrdersStatus,
} from '../../../redux/actions/workQueue/allOrdersActions';
import {
  invalidateQuery,
  useGetVehicleOrdersQuery,
  useRepairOrdersMutation,
} from '../../../redux/rtk/orders.services';
import {
  useGetVehiclePhotoCountMutation,
  useStatusUpdateMutation,
} from '../../../redux/rtk/vendors.services';
import { currentUserCompanyId } from '../../../redux/selectors/userDataSelectors';
import {
  allOrdersSortEnabled,
  allOrdersSortBy,
  allOrdersSortField,
} from '../../../redux/selectors/workQueue/allOrdersSelectors';
import { BODY_BUILDER_BY_TAB, SELECTORS_BY_TAB } from '../allOrders/utils';
import EditRo from './EditRo';
import EmptyState from './EmptyState';
import InProcessStatusDialog from './StatusModals/InProcessDialog';
import { Modal } from './StatusModals/Modal';
import WholeSaleDialog from './StatusModals/WholeSaleDialog';

export default function VehicleView({ tab, optionalSearchQuery }) {
  const { t } = useTranslation('orders');
  const [pageNumber, setPageNumber] = useState(1);
  const [repairOrders, { isLoading: isUpdating }] = useRepairOrdersMutation();
  const [roNumber, setRoNumber] = useState({
    open: false,
    roNumber: null,
    checked: false,
    packageValue: null,
  });
  const [orderId, setOrderId] = useState(null);
  const [details, setDetails] = useState({});
  const sortEnabled = useSelector(allOrdersSortEnabled);
  const sortColumnField = useSelector(allOrdersSortField);
  const sortColumnBy = useSelector(allOrdersSortBy);
  const companyId = useSelector(currentUserCompanyId);
  const [sortFields, setSortFields] = useState({});
  const history = useHistory();
  const dispatch = useDispatch();
  const filters = useSelector(SELECTORS_BY_TAB[tab].body(companyId));
  const [openSnackPdf, setOpenSnackPdf] = useState(false);
  const closeSnackPdf = () => setOpenSnackPdf(false);
  const [errorSnackBar, setErrorSnackBar] = useState(false);
  const { data, isFetching, isLoading, isError } = useGetVehicleOrdersQuery(
    BODY_BUILDER_BY_TAB[tab](
      companyId,
      pageNumber,
      filters,
      optionalSearchQuery,
      sortEnabled,
      sortColumnBy,
      sortColumnField
    )
  );
  const isSortEnabledForTab = SELECTORS_BY_TAB[tab].isSortEnabled;
  const [currentOrder, setCurrentOrder] = useState(null);
  const [wholeSaleStatusModal, setWholeSaleStatusModal] = useState(false);
  const [inProcessStatusModal, setInProcessStatusModal] = useState(false);
  const [statusSuccess, setStatusSuccess] = useState(false);
  const [photoCount, setPhotoCount] = useState({});
  const [statusError, setStatusError] = useState(false);
  const [orderStausErrorMessage, setOrderStausErrorMessage] = useState(false);
  const [woStatusUpdate, { isLoading: isStatusUpdateLoading }] =
    useStatusUpdateMutation();
  const [putVehicle, { isLoading: isLoadingVehicle }] =
    useGetVehiclePhotoCountMutation();
  const orders = { ...data };
  const isLoadingOrFetching = isLoading || isFetching;

  isLoadingOrFetching &&
    pageNumber === 1 &&
    orders?.vehicleOrders &&
    (orders.vehicleOrders = []);

  useEffect(() => {
    setPageNumber(1);
  }, [setPageNumber, tab]);

  useEffect(() => {
    if (isError) {
      setErrorSnackBar(true);
      dispatch(setAllOrdersStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const getWOStatement = order => {
    setOpenSnackPdf(true);
    dispatch(
      getWorkOrderToPdf({
        order_id: order.orderId,
      })
    ).then(({ payload: pdfString }) => {
      DownloadPdf(pdfString, order.orderId);
    });
  };

  const openEditRo = order => {
    if (order) {
      const relatedOrders = (order.relatedOrders || []).reduce((acc, ro) => {
        if (
          ro.step !== ProcessStep.DECLINED &&
          ro.step !== ProcessStep.CANCELED
        ) {
          const activeOrder = { ...ro };
          const activeOrderServices = activeOrder.services.filter(
            z => !z.declinedAt
          );
          activeOrder.services = activeOrderServices;
          acc.push(activeOrder);
        }
        return acc;
      }, []);
      setRoNumber({
        open: true,
        checked: false,
        roNumber: order.ro,
        packageValue: order.package.totalPrice,
      });
      setOrderId(order.orderId);
      setDetails(
        sortGroupedByDate(
          groupByDateAndType({
            packages: relatedOrders.filter(
              x => x.orderType === OrderTypes.RECONDITIONING
            ),
            estimates: relatedOrders.filter(
              x => x.orderType === OrderTypes.ESTIMATE
            ),
          })
        )
      );
    }
  };

  const onSaveRo = async order => {
    const oneRo = getValueIfPresent(order, 'oneRo');
    let repairOrder = swapKeysAndValues(
      Object.fromEntries(
        Object.entries(order).filter(
          ([key, value]) =>
            !key.startsWith('packages') &&
            !key.startsWith('estimates') &&
            !key.startsWith('oneRo')
        )
      ),
      oneRo
    );

    if (oneRo) {
      const relatedOrderIds = Object.values(repairOrder).flat();
      repairOrder = { [oneRo]: relatedOrderIds };
    }

    return await repairOrders({
      orderId,
      repairOrder: repairOrder,
    })
      .unwrap()
      .then(result => {
        if (result)
          setRoNumber({
            open: false,
            roNumber: null,
            checked: false,
            packageValue: null,
          });
      });
  };

  const handleColumnSort = (fieldValue, sortBy) => {
    const action = sortBy?.toLowerCase();
    const sortData = {};
    const currentAction = sortFields?.[fieldValue]?.[action];
    const sortField = fieldValue === 'dateOrdered' ? 'date' : fieldValue;
    sortData[fieldValue] = {
      asc: false,
      desc: false,
      [action]: !currentAction,
    };
    setSortFields(sortData);
    if (sortData?.[fieldValue]?.[action]) {
      setPageNumber(1);
      dispatch(
        setAllOrdersSortDetails({
          sortEnabled: true,
          sortBy: action,
          sortField: sortField,
        })
      );
    } else {
      setPageNumber(1);
      dispatch(
        setAllOrdersSortDetails({
          sortEnabled: false,
        })
      );
    }
  };

  const resetToInitialPage = () => {
    const body = BODY_BUILDER_BY_TAB[tab](
      companyId,
      pageNumber,
      filters,
      optionalSearchQuery,
      sortEnabled,
      sortColumnBy,
      sortColumnField
    );
    setPageNumber(1);
    dispatch(invalidateQuery(body));
  };

  const handleMoveToWholeSale = order => {
    setWholeSaleStatusModal(true);
    setCurrentOrder(order);
  };

  const handleMoveToInProcess = order => {
    setInProcessStatusModal(true);
    setCurrentOrder(order);
  };

  const moveOrderToWholeSaleStatus = async payload => {
    const statusBody = {
      orderId: currentOrder?.orderId,
      status: 'Wholesale',
      additionalComments: payload?.comments,
    };
    try {
      await woStatusUpdate(statusBody).unwrap();
      setStatusSuccess(true);
      setOrderStausErrorMessage('Success, the status has been updated.');
      resetToInitialPage();
    } catch (err) {
      setStatusError(true);
      setOrderStausErrorMessage(
        'Uh Oh! There was an unexpected error in updating order status. Please try again.'
      );
    }
    setWholeSaleStatusModal(false);
  };

  const moveOrderToInProcessStatus = async () => {
    const orderBody = {
      orderId: currentOrder?.orderId,
      status: 'In Process',
    };
    try {
      await woStatusUpdate(orderBody).unwrap();
      setStatusSuccess(true);
      setOrderStausErrorMessage('Success, the status has been updated.');
      resetToInitialPage();
    } catch (err) {
      setStatusError(true);
      setOrderStausErrorMessage(
        'Uh Oh! There was an unexpected error in updating order status. Please try again.'
      );
    }
    setInProcessStatusModal(false);
  };

  const closeWholeSaleStatusModal = () => {
    setWholeSaleStatusModal(false);
  };
  const closeInProcessStatusModal = () => {
    setInProcessStatusModal(false);
  };

  const closeSuccessModal = () => {
    setStatusError(false);
    setStatusSuccess(false);
  };

  const handleApi = async row => {
    if (!row?.vin) return;
    const apiBody = {
      orderId: row?.orderId,
      dealerId: row?.dealershipId,
    };
    try {
      const photoCountResponse = await putVehicle({
        vinOrStockNumber: row.vin,
        body: apiBody,
      }).unwrap();
      const photoCountValue = row?.vehicle?.showPhotoCount
        ? photoCountResponse?.photosCount || 0
        : null;
      setPhotoCount(prev => ({ ...prev, [row.orderId]: photoCountValue }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error in fetching photo count', err);
      setPhotoCount(prev => ({ ...prev, [row.orderId]: null }));
    }
  };

  return (
    <>
      {wholeSaleStatusModal && (
        <WholeSaleDialog
          open={wholeSaleStatusModal}
          onClose={closeWholeSaleStatusModal}
          onSave={moveOrderToWholeSaleStatus}
          isLoading={isStatusUpdateLoading}
        />
      )}
      {inProcessStatusModal && (
        <InProcessStatusDialog
          open={inProcessStatusModal}
          onClose={closeInProcessStatusModal}
          onSave={moveOrderToInProcessStatus}
          isLoading={isStatusUpdateLoading}
        />
      )}
      <Modal
        type="success"
        open={statusSuccess}
        onClose={closeSuccessModal}
        message={orderStausErrorMessage}
      />
      <Modal
        type="error"
        open={statusError}
        onClose={closeSuccessModal}
        message={orderStausErrorMessage}
      />
      <GenSnackBar
        title={t('getOrders.description')}
        open={errorSnackBar}
        type="error"
        onClose={() => setErrorSnackBar(false)}
      />
      <WorkOrderForVehicle
        loading={isLoadingOrFetching}
        onLoadMore={() => {
          if (isError) {
            dispatch(setAllOrdersHasMore({ hasMore: false }));
            return;
          }
          setPageNumber(pageNumber + 1);
        }}
        data={orders}
        history={history}
        enableOrderTagActions={false}
        isAddEstimateDisabled={() => true}
        enablePinToTop={false}
        enableTechnicianIcon={false}
        exportWO={order => getWOStatement(order)}
        enableExportWO={SELECTORS_BY_TAB[tab].enableExportWO}
        enableEditRo={SELECTORS_BY_TAB[tab].enableEditRo}
        editRo={order => openEditRo(order)}
        handleColumnSort={handleColumnSort}
        sortFields={sortFields}
        isSortEnabled={isSortEnabledForTab}
        enableVehicleCardView
        enableStatusFilter
        enableStatusColumn={SELECTORS_BY_TAB[tab].enableStatusColumn}
        moveToWholeSaleFn={handleMoveToWholeSale}
        moveToInProcessFn={handleMoveToInProcess}
        handleApi={handleApi}
        apiLoading={isLoadingVehicle}
        photoCount={photoCount}
      />
      <EditRo
        open={roNumber.open}
        ro={roNumber.roNumber}
        packageTotalValue={roNumber.packageValue}
        title={t('editRo.title')}
        onClose={() =>
          setRoNumber({
            open: false,
            roNumber: null,
            checked: false,
            packageValue: null,
          })
        }
        orderDetails={details}
        onSave={order => onSaveRo(order)}
        loading={isUpdating}
        setRoNumber={setRoNumber}
        checked={roNumber.checked}
      />
      <EmptyState
        isLoading={isLoadingOrFetching}
        show={!isLoadingOrFetching && !(orders?.vehicleOrders ?? []).length}
        img={Empty}
        title={t(SELECTORS_BY_TAB[tab].emptyTitle)}
        subtitle={t(SELECTORS_BY_TAB[tab].emptySubTitle)}
      />
      <GenSnackBar
        open={openSnackPdf}
        message={t('alertExportPdf')}
        onClose={closeSnackPdf}
        color="#323232"
        icon={false}
      />
    </>
  );
}
